import { ReactNode } from 'react'

import {
  Box,
  Checkbox,
  SxProps,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import { Theme } from '@/styles/theme'

import { ColumnVisibilityItem } from '../hooks/useColumnVisibility'
import { BASE_TABLE_TEST_IDS, COLUMN_CELL_TYPE } from '../TableBase.constants'
import { Column, SortOrder } from '../TableBase.types'
import { ColumnVisibility } from './ColumnVisibility'

type Props<Data extends Record<string, unknown>, SortAttributes extends string> = {
  columns: Column<Data, SortAttributes>[]
  withCheckboxes?: boolean
  rowActions?: ReactNode
  sortAttribute?: SortAttributes
  sortOrder?: SortOrder
  handleSortChange: (newSortAttribute: SortAttributes) => void
  isAnyRowChecked: boolean
  areAllRowsChecked: boolean
  handleCheckAll: (checked: boolean) => void
  columnVisibility: ColumnVisibilityItem[] | false
  toggleColumnVisibility: (columnId: string) => void
  checkColumnVisibility: (column: string | Column<Data, SortAttributes>) => boolean
  hasRowDetails?: boolean
  rowSx?: SxProps<Theme>
}

export const Head = <Data extends Record<string, unknown>, SortAttributes extends string>({
  columns,
  withCheckboxes,
  rowActions,
  sortAttribute,
  sortOrder,
  handleSortChange,
  isAnyRowChecked,
  areAllRowsChecked,
  handleCheckAll,
  columnVisibility,
  toggleColumnVisibility,
  checkColumnVisibility,
  hasRowDetails,
  rowSx
}: Props<Data, SortAttributes>) => {
  return (
    <TableHead data-testid={BASE_TABLE_TEST_IDS.tableHead}>
      <TableRow
        sx={{
          ...rowSx,
          position: 'relative',
          '& > :first-of-type': {
            pl: withCheckboxes ? 0.85 : 2
          },
          '& > :nth-of-type(2)': {
            pl: withCheckboxes ? 1.25 : 2
          },
          '& > :last-child': {
            pr: 1
          }
        }}
      >
        {(withCheckboxes || hasRowDetails) && (
          <TableCell padding="checkbox">
            {withCheckboxes && (
              <Checkbox
                data-testid={BASE_TABLE_TEST_IDS.selectAllCheckbox}
                indeterminate={isAnyRowChecked && !areAllRowsChecked}
                checked={areAllRowsChecked}
                onChange={(e) => handleCheckAll(e.target.checked)}
                sx={{ py: 1 }}
                color="secondary"
              />
            )}
          </TableCell>
        )}

        {columns
          .filter((column) => checkColumnVisibility(column.id))
          .map((column) => (
            <TableCell
              key={String(column.id)}
              align={column.align}
              sortDirection={sortAttribute === column.sortAttribute ? sortOrder : undefined}
              sx={{
                py: 1,
                px: column.type === COLUMN_CELL_TYPE.COMPONENT ? 1 : undefined,
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
              padding={column.type === COLUMN_CELL_TYPE.COMPONENT ? 'none' : 'normal'}
              data-testid={`${BASE_TABLE_TEST_IDS.tableHeadCell}-${column.id}`}
            >
              <ConsoleTooltip title={column.tooltip} placement="top" arrow distance={9}>
                {column.sortAttribute && !column.disableSorting ? (
                  <TableSortLabel
                    data-testid={`${BASE_TABLE_TEST_IDS.tableSortLabel}-${column.id}`}
                    active={sortAttribute === column.sortAttribute}
                    direction={sortOrder}
                    onClick={() => handleSortChange(column.sortAttribute as SortAttributes)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  <span style={{ userSelect: 'none', cursor: 'default' }}>{column.label}</span>
                )}
              </ConsoleTooltip>
            </TableCell>
          ))}

        {rowActions && <TableCell align="right" padding="none" />}

        {columnVisibility && (
          <Box
            component="td"
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            <ColumnVisibility
              columnVisibility={columnVisibility}
              toggleColumnVisibility={(columnId) => toggleColumnVisibility(columnId)}
            />
          </Box>
        )}
      </TableRow>
    </TableHead>
  )
}
